import { useState, useEffect, useRef } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { ArrowPathIcon, XCircleIcon } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'
import { useActivation } from '../hooks/useActivation'
import { parseCabinetQrCode } from '../lib/qrCodeParser'
import { useDebounceScan } from '../hooks/useDebounceScan'

function StatusOverlay({ status, message }) {
  const bgColor = {
    ERROR: 'bg-red-500/90',
    VALIDATING: 'bg-black/80',
  }[status]

  const icon = {
    ERROR: <XCircleIcon className="w-24 h-24 text-white" />,
    VALIDATING: <ArrowPathIcon className="w-24 h-24 text-blue-500 animate-spin" />,
  }[status]

  return (
    <div className={`absolute inset-0 ${bgColor} flex flex-col justify-center items-center p-4 z-10 rounded-lg`}>
      {icon}
      <p className="text-center text-white font-bold text-2xl mt-4">{message}</p>
    </div>
  )
}

function CabinetSetupPage() {
  const { setCabinetData } = useActivation()
  const { checkAndScan } = useDebounceScan(3000)
  const [status, setStatus] = useState('SCANNING')
  const [cameraError, setCameraError] = useState(null)
  const scannerRef = useRef(null)
  const statusRef = useRef(status)

  useEffect(() => {
    statusRef.current = status
  }, [status])

  const isScannerRunningRef = useRef(false)

  useEffect(() => {
    let isMounted = true
    const qrScanner = new Html5Qrcode('cabinet-qr-reader', { verbose: false })
    scannerRef.current = qrScanner

    const startScanner = async () => {
      try {
        await Html5Qrcode.getCameras()
        if (!isMounted || !scannerRef.current) return

        await qrScanner.start(
          { facingMode: 'environment' },
          { 
            fps: 5, 
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0 
          },
          (decodedText) => {
            if (statusRef.current !== 'SCANNING' || !isMounted) return

            checkAndScan(decodedText, (text) => {
              const parsed = parseCabinetQrCode(text)
              if (!parsed) {
                toast.error('QR de gabinete invalido. Use formato serial,slot.')
                return
              }

              setStatus('VALIDATING')

              window.setTimeout(() => {
                if (isMounted) {
                  setCabinetData(parsed.cabinet_serial, parsed.slot_position)
                  toast.success('Gabinete conectado. Pronto para ativacao.')
                }
              }, 500)
            })
          },
          () => {},
        )

        // Mark as running ONLY after successful start
        if (isMounted) {
          isScannerRunningRef.current = true
        }
      } catch (error) {
        if (!isMounted) return
        
        const message =
          error.name === 'NotAllowedError' ? 'Permissao da camera negada.' : error.message
        setCameraError(message)
        setStatus('ERROR')
      }
    }

    startScanner()

    return () => {
      isMounted = false
      const scanner = scannerRef.current
      
      if (scanner) {
        scannerRef.current = null
        
        try {
           if (isScannerRunningRef.current) {
             scanner.stop()
               .catch(err => console.warn('Scanner stop warning:', err))
               .finally(() => {
                 scanner.clear().catch(err => console.warn('Scanner clear warning:', err))
               })
           } else {
             scanner.clear().catch(err => console.warn('Scanner clear warning:', err))
           }
        } catch (e) {
            console.warn("Erro ao parar o scanner durante a desmontagem", e);
        }
        
        isScannerRunningRef.current = false
      }
    }
  }, [setCabinetData])

  const showOverlay = status !== 'SCANNING' && !cameraError

  return (
    <div className="min-h-screen bg-[#0d1117] text-white flex flex-col items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md mx-auto">
        <header className="text-center mb-6">
          <h1 className="text-lg text-gray-300 font-semibold">Cadastro de Gabinete</h1>
          <p className="text-xl font-bold tracking-wider text-gray-100 h-7">Escaneie o QR do Gabinete</p>
        </header>

        <main>
          <div className="relative w-full aspect-square bg-black rounded-lg border-2 border-gray-700 flex items-center justify-center overflow-hidden">
             {/* 
                We need to ensure the scanner fills this container. 
                Html5Qrcode tends to inject a video element. 
                We can control its size via the config or CSS.
                The key is 'overflow-hidden' on the parent (already present) 
                and ensuring the child fills it.
             */}
            <div id="cabinet-qr-reader" className="w-full h-full [&>video]:object-cover [&>video]:w-full [&>video]:h-full" />

            {cameraError && <StatusOverlay status="ERROR" message={cameraError} />}
            {showOverlay && <StatusOverlay status="VALIDATING" message="Validando gabinete..." />}

            {status === 'SCANNING' && !cameraError && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div
                  className="w-[250px] h-[250px]"
                  style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)' }}
                >
                  <div className="relative w-full h-full border-2 border-green-400/70 rounded-lg" />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default CabinetSetupPage
